import FormGroup from "legacy/FormGroup"
import LegacyButton from "legacy/LegacyButton"
import {
  afterSetState,
  centsToDollars,
  centsToDollarsRaw,
  dollarsToCents,
  humanize,
} from "legacy/helpers.es6"
import { validatorFactory } from "legacy/validatorFactory"
import _ from "lodash"
import PropTypes from "prop-types"
import React from "react"

import Modal from "src/components/Modal"

/**
 * RefundDistributionModal provides the form for issuing a refund distribution.
 * @param {string} endpoint - The endpoint to use when submitting the form.
 * @param {number} total_refund_amount - The total amount, in cents, to be refunded to the customer.
 * @param {number} unpaid_invoice_balance - The total amount, in cents, to be credited back to the customer.
 * @param {number} final_refundable_amount - The total amount, in cents, to be refunded to the customer, less the unpaid invoice balance.
 * @param {Object[]} payment_methods - An array of objects representing the different payment methods requiring refund distribution.
 * @param {string} payment_methods[].payment_method - One of "credit", "cash", "check", "ach"
 * @param {string} payment_methods[].max_amount - The maximum refund to be distributed to this payment method.
 * @param {string} payment_methods[].current_amount - The current refund to be distributed to this payment method on form submit.
 * @param {string} [payment_methods[].card_last4] - The last four numbers of the credit card, if applicable.
 * @param {string} [payment_methods[].card_brand] - The brand of the credit card, if applicable.
 * @param {string} [payment_methods[].stripe_payment_method_id] - A unique id for the card/bank account, if applicable.
 */

class RefundDistributionModal extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      showModal: false,
      processing: false,
      paymentMethods: this.initializePaymentMethods(),
      processingErrorMessage: "",
      distributionErrorMessage: "",
    }
    this.initializePaymentMethods = this.initializePaymentMethods.bind(this)
    this.buildPaymentIntentsFromFormState =
      this.buildPaymentIntentsFromFormState.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleSubmitRefundDistribution =
      this.handleSubmitRefundDistribution.bind(this)
    this.toggleShowModal = this.toggleShowModal.bind(this)
    this.refundFormInputLabel = this.refundFormInputLabel.bind(this)
    this.validator = validatorFactory({ autoForceUpdate: this })
  }

  // Functions for managing state
  toggleShowModal(value) {
    this.setState({
      showModal: value,
      distributionErrorMessage: "",
      processingErrorMessage: "",
    })
  }

  handleInputChange(event, formId) {
    const newCurrentAmount = event.target.value
    const paymentMethods = this.state.paymentMethods
    const paymentMethod = paymentMethods.find(
      (method) => method.formId === formId
    )
    const paymentMethodIndex = paymentMethods.indexOf(paymentMethod)
    paymentMethods[paymentMethodIndex] = {
      ...paymentMethod,
      current_amount: newCurrentAmount,
    }
    this.setState({
      paymentMethods,
      distributionErrorMessage: "",
      processingErrorMessage: "",
    })
    afterSetState(() => {
      this.validator.showMessages()
    })
  }

  initializePaymentMethods() {
    // The unique formId helps the payment method play nicely with <FormGroup /> and this.handleInputChange
    return this.props.payment_methods.map((paymentMethod) => ({
      ...paymentMethod,
      current_amount: centsToDollarsRaw(paymentMethod.current_amount),
      formId: _.uniqueId(`${paymentMethod.payment_method}_`),
    }))
  }

  // Helper function for building api request payload
  buildPaymentIntentsFromFormState() {
    return this.state.paymentMethods.map((method) => {
      const paymentIntent = {
        payment_method: method.payment_method,
        amount: dollarsToCents(method.current_amount) * -1,
      }
      if (
        method.payment_method === "credit" ||
        method.payment_method === "automated_ach"
      ) {
        paymentIntent.stripe_payment_method_id = method.stripe_payment_method_id
      }
      return paymentIntent
    })
  }

  // Submit function; builds and submits request payload. Invoked on button press.
  handleSubmitRefundDistribution() {
    this.validator.showMessages()
    if (this.validator.allValid()) {
      const requestPayload = {
        payment_intents: this.buildPaymentIntentsFromFormState(),
      }
      const totalInputDistributionInCents =
        requestPayload.payment_intents.reduce(
          (a, b) => ({ amount: a.amount + b.amount * -1 }),
          { amount: 0 }
        )
      if (
        totalInputDistributionInCents.amount !==
        this.props.final_refundable_amount
      ) {
        this.setState({
          distributionErrorMessage: `The total distribution must amount to ${centsToDollars(
            this.props.final_refundable_amount
          )}. Currently, the total is ${centsToDollars(
            totalInputDistributionInCents.amount
          )}.`,
        })
      } else {
        this.setState({ processing: true })
        $.ajax({
          url: this.props.endpoint,
          data: JSON.stringify(requestPayload),
          type: "POST",
          dataType: "json",
          contentType: "application/json",
          success: (data) => {
            this.setState({ processing: false })
            this.toggleShowModal(false)
            location.reload()
          },
          error: (data) => {
            this.setState({
              processing: false,
              distributionErrorMessage: "",
              processingErrorMessage: "Something went wrong!",
            })
          },
        })
      }
    }
  }

  // Individual pieces of components, meant to be composed together
  renderExplanation() {
    return (
      <div>
        <p>
          Multiple payment methods were used to pay for this reservation. Please
          select how much money should be refunded to each payment method.
        </p>
        <br />
        <p>
          You will be applying a
          <strong> {centsToDollars(this.props.unpaid_invoice_balance)} </strong>
          credit to unpaid invoice(s) and refunding
          <strong>
            {" "}
            {centsToDollars(this.props.final_refundable_amount)}{" "}
          </strong>
          to the customer.
        </p>
      </div>
    )
  }

  renderCreditsApplied() {
    return (
      <div>
        <h2 className={"text-lg font-bold"}>
          Credits applied to unpaid invoice(s)
        </h2>
        <FormGroup
          inputType="currency"
          label="Scheduled Payments"
          labelClassName="mt-2.5"
          value={centsToDollarsRaw(this.props.unpaid_invoice_balance)}
          disabled={true}
        />
      </div>
    )
  }

  refundFormInputLabel(paymentMethod) {
    const isCredit = paymentMethod.payment_method === "credit"
    const isAutomatedAch = paymentMethod.payment_method === "automated_ach"
    return (
      <div className={"flex w-full flex-row items-center justify-between"}>
        {(isCredit || isAutomatedAch) && (
          <div className={"flex items-center"}>
            <img
              src={paymentMethod.logo}
              className="m-0 mb-1 mr-2 h-6 w-6"
              alt={`${paymentMethod.brand} logo`}
            />
            <p className={"mb-1"}>{paymentMethod.last4}</p>
          </div>
        )}
        {!isCredit && !isAutomatedAch && (
          <div className={"flex"}>
            <p className={"mb-1"}>{humanize(paymentMethod.payment_method)}</p>
          </div>
        )}
        <div className={"flex-end flex"}>
          <p className={"mb-2 text-gray-600"}>
            {`Originally charged: ${centsToDollars(paymentMethod.max_amount)}`}
          </p>
        </div>
      </div>
    )
  }

  renderRefundsToCustomer() {
    return (
      <div>
        <h2 className={"text-lg font-bold"}>Refunds to customer</h2>
        {this.state.paymentMethods.map((paymentMethod) => {
          const isDisabled = paymentMethod.payment_method === "dockwa_dollar"
          return (
            <div key={paymentMethod.formId}>
              <div className={"flex"}>
                <FormGroup
                  name={paymentMethod.formId}
                  customLabelComponent={this.refundFormInputLabel(
                    paymentMethod
                  )}
                  labelClassName="w-full"
                  inputType="currency"
                  className="w-full"
                  value={paymentMethod.current_amount}
                  placeholder="0.00"
                  onChange={(event) =>
                    this.handleInputChange(event, paymentMethod.formId)
                  }
                  validation={this.validator.message(
                    paymentMethod.formId,
                    paymentMethod.current_amount,
                    `required|currency|maxDecimalPrecision:2|max:${centsToDollarsRaw(
                      paymentMethod.max_amount
                    )},num|min:0,num`,
                    {
                      messages: {
                        default: `Must be between $0.00 and ${centsToDollars(
                          paymentMethod.max_amount
                        )}`,
                      },
                    }
                  )}
                  disabled={isDisabled}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  renderErrorMessages() {
    return (
      <div>
        {this.state.processingErrorMessage ? (
          <p className="text-status-red col-span-full">
            {this.state.processingErrorMessage}
          </p>
        ) : (
          ""
        )}
        {this.state.distributionErrorMessage ? (
          <p className="text-status-red col-span-full">
            {this.state.distributionErrorMessage}
          </p>
        ) : (
          ""
        )}
      </div>
    )
  }

  // Components (partly composed of the above pieces) to pass to Modal
  renderHeaderContent() {
    return <Modal.Header title="Edit Refund Distribution" />
  }

  renderBodyContent() {
    return (
      <Modal.Body>
        {this.renderExplanation()}
        {this.renderCreditsApplied()}
        {this.renderRefundsToCustomer()}
        {this.renderErrorMessages()}
      </Modal.Body>
    )
  }

  renderFooterContent() {
    return (
      <Modal.Footer
        cancelBtnText="Cancel"
        confirmBtnText="Save Distribution"
        confirmBtnLoading={this.state.processing}
        onClose={() => this.toggleShowModal(false)}
        onSubmit={this.handleSubmitRefundDistribution}
      />
    )
  }

  render() {
    return (
      <div>
        <LegacyButton
          type="secondary"
          className="btn-sm"
          onClick={() => this.toggleShowModal(true)}
        >
          Edit
        </LegacyButton>
        <Modal
          isOpen={this.state.showModal}
          onClose={() => this.toggleShowModal(false)}
        >
          {this.renderHeaderContent()}
          {this.renderBodyContent()}
          {this.renderFooterContent()}
        </Modal>
      </div>
    )
  }
}

RefundDistributionModal.propTypes = {
  endpoint: PropTypes.string.isRequired,
  total_refund_amount: PropTypes.number.isRequired,
  unpaid_invoice_balance: PropTypes.number.isRequired,
  final_refundable_amount: PropTypes.number.isRequired,
  payment_methods: PropTypes.arrayOf(
    PropTypes.shape({
      payment_method: PropTypes.string.isRequired,
      max_amount: PropTypes.number.isRequired,
      current_amount: PropTypes.number.isRequired,
      card_last4: PropTypes.string,
      card_brand: PropTypes.string,
      stripe_payment_method_id: PropTypes.string,
      logo: PropTypes.string,
      last4: PropTypes.string,
      brand: PropTypes.string,
    })
  ).isRequired,
}

export default RefundDistributionModal
